<template>
  <div class="bg-gray-21 px-3">
    <div class="d-flex align-items-center justify-content-between mb-4 ml-n3">
      <div class="d-flex align-items-center">
        <circle-icon
          shadow
          size="31px"
          icon-size="20px"
          color="#fff"
          icon-color="#fff"
        >
          <template #icon>
            <img src="@/assets/icons/folder-grid.svg" />
          </template>
        </circle-icon>
        <span class="color-black-1 font-16 font-weight-bold ml-2">
          {{ $t('DocumentsRequiredForInvestigation') }}
        </span>
      </div>
      <action-button
        v-if="canRetryAll"
        class="outline-danger px-4 py-2 mr-3"
        i="fa fa-redo"
        :text="$t('retryAll')"
        @click="retryAll"
        :disabled="processing"
      />
    </div>
    <div
      v-for="(item, index) in documents"
      :key="`doc-item-${index}`"
      class="row bg-white p-3 rounded mb-2"
    >
      <div class="col-3">
        <div class="color-gray-18 font-14">{{ $t('DocType') }}:</div>
        <div>
          <span
            v-if="item.documentCategoryCode"
            class="color-black font-14 font-weight-bold"
          >
            {{ item.documentCategoryCode }} -
          </span>
          <span class="color-black font-14">
            {{ item.documentCategoryName }}
          </span>
        </div>
      </div>
      <div class="col-3">
        <div v-if="item.filename">
          <div class="color-gray-18 font-14">{{ $t('Document') }}:</div>
          <span class="color-black font-14">
            {{ item.filename }}
          </span>
        </div>
      </div>

      <div class="col-2" v-if="item.documentId">
        <div class="color-gray-18 font-14">{{ $t('stampingStatus') }}:</div>
        <div>
          <span
            class="badge"
            :class="{
              'badge-warning':
                item.stampingStatus === 'Pendiente' && !item.processingError,
              'badge-success': item.stampingStatus === 'Ponchado',
              'badge-info': item.stampingStatus === 'Procesando',
              'badge-danger': item.processingError,
            }"
          >
            <span v-if="item.processingError">
              <i class="fas fa-exclamation-triangle"></i>
              Error
            </span>
            <span v-else>
              {{ item.stampingStatus || 'Pendiente' }}
            </span>
          </span>
          <i
            v-if="item.processingError"
            class="fas fa-info-circle text-danger ml-1 cursor-pointer"
            :title="item.processingError"
            v-b-tooltip.hover
          ></i>
          <div v-if="item.processingAttempts > 0" class="mt-1">
            <small class="text-muted">
              Intentos: {{ item.processingAttempts }}
            </small>
          </div>
        </div>
      </div>

      <div class="col-2" v-if="item.documentId && canManageStamping">
        <div class="color-gray-18 font-14">
          {{
            item.processingError
              ? $t('lastAttempt') + ':'
              : $t('stampingDateTime') + ':'
          }}
        </div>
        <div>
          <span
            v-if="item.stampingDate && !item.processingError"
            class="color-black font-14"
          >
            <i class="far fa-clock mr-1"></i>
            {{ formatStampingDate(item.stampingDate) }}
          </span>
          <span
            v-else-if="item.updatedDate && item.processingError"
            class="color-danger font-14"
          >
            <i class="far fa-clock mr-1"></i>
            {{ formatStampingDate(item.updatedDate) }}
          </span>
          <span v-else class="color-gray-18 font-14">
            <i>{{ $t('notStampedYet') }}</i>
          </span>
        </div>
      </div>

      <div class="col d-flex align-items-center">
        <span
          v-if="!item.documentId && !isReadOnly"
          class="color-red-1 font-14"
        >
          {{ $t('Required') }}*
        </span>
      </div>
      <div class="col d-flex align-items-center">
        <b-form-checkbox
          v-if="!item.hiddenPublic && !isReadOnly"
          :disabled="item.filename !== undefined"
          v-model="item.isPublic"
          class="d-block"
        >
          <span class="color-black-3 font-14 font-weight-bold">
            {{ $t('IsPublic') }}
          </span>
        </b-form-checkbox>
      </div>
      <div class="col d-flex align-items-center justify-content-end">
        <input
          :id="`fileUpload${item.id}`"
          :ref="`refFileUpload${item.id}`"
          v-show="false"
          accept=".pdf"
          type="file"
          @change="updateFileChange($event, item)"
        />
        <action-button
          v-if="!item.documentId && !isReadOnly"
          :text="$t('ClickToUploadDocument')"
          class="warning outline-warning px-4 mr-2"
          i="fal fa-cloud-upload"
          @click="onAddFileHandler(item)"
        />
        <action-button
          v-if="item.documentId && !isReadOnly"
          class="btn-outline-success delete-action mr-2"
          i="fal fa-minus-circle"
          @click="onDeleteHandler(item)"
        />
        <action-button
          v-if="item.documentId"
          class="info px-4 mr-2 view-action"
          i="fa fa-eye"
          title="Ver documento"
          @click="onViewHandler(item.documentId)"
        />
        <action-button
          v-if="item.documentId"
          class="warning px-4 mr-2 download-action"
          i="fa fa-cloud-download"
          @click="onDownloadHandler(item.documentId)"
        />
        <action-button
          class="outline-danger px-3 mr-2 retry-action"
          :class="{ 'invisible-button': !canRetryStamping(item) }"
          i="fa fa-redo"
          :title="$t('retryStamping')"
          @click="retryStamping(item)"
          :disabled="processing"
        />
      </div>
    </div>
  </div>
</template>

<script>
//libs
import ActionButton from 'ActionButton';

//components
import { CircleIcon } from '@/components/common';

//utils
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin';
import { utilityMixin } from '@/mixins/utility.mixin';
import { mapState, mapGetters } from 'vuex';
import { name as userModuleName } from '@/store/modules/user';

//services
import {
  downloadItemAttachment,
  saveItemAttachment,
  removeItemAttachment,
  executeManualStamping,
  viewItemAttachment,
} from '@/services/app.api.js';
import { getIsLockedDocuments } from '@/modules/tickets/api.js';
import objectToFormData from 'object-to-formdata';
import { i18n } from '@/i18n';
import moment from 'moment';

export default {
  name: 'Documents',
  mixins: [alertMixin, utilityMixin],
  components: {
    ActionButton,
    CircleIcon,
  },
  props: {
    itemId: {
      type: Number,
      required: true,
    },
    documents: {
      type: Array,
      default: () => [],
    },
    isReadOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      processing: false,
    };
  },
  computed: {
    ...mapState('user', ['currentUser']),
    ...mapGetters(userModuleName, {
      userRole: 'role',
    }),

    itemStatus() {
      return this.itemModel?.requestStatusId;
    },

    canManageStamping() {
      const role =
        this.userRole || this.roleName?.() || this.currentUser?.role || '';
      const normalizedRole = role ? String(role).toUpperCase().trim() : '';
      const hasAccess = [
        'SUPERVISOR',
        'ADMIN',
        'ADMINISTRADOR',
        'TECHNICAL',
      ].includes(normalizedRole);

      return hasAccess;
    },

    isRedaccionCartaOrTesis() {
      return this.itemStatus === 23 || this.itemStatus === 27;
    },

    canRetryAll() {
      if (
        !this.canManageStamping ||
        this.itemStatus !== 24 ||
        this.isRedaccionCartaOrTesis
      ) {
        return false;
      }

      return this.documents.some(
        (d) =>
          d.documentId &&
          (d.stampingStatus === 'Pendiente' || d.processingError)
      );
    },
  },
  methods: {
    async onViewHandler(documentId) {
      if (documentId) {
        try {
          const blobUrl = await viewItemAttachment(documentId);
          window.open(blobUrl, '_blank');
        } catch (error) {
          this.ShowToast('Error', 'No se pudo abrir el documento', 'error');
        }
      }
    },
    async onDownloadHandler(documentId) {
      const { data } = await getIsLockedDocuments(documentId);
      const isLocked = data;

      if (isLocked) {
        this.ShowToast(i18n.t('Alert'), `${i18n.t('LockedFiles')}.`, 'warning');
        return;
      }
      if (documentId) {
        await downloadItemAttachment(documentId).catch((error) => {
          this.ShowToast('', error.response.data.message, 'error');
        });
      }
    },
    onAddFileHandler(item) {
      document.getElementById(`fileUpload${item.id}`).click();
    },
    onRefreshHandler() {
      this.$emit('on-refresh-document');
    },
    async updateFileChange(event, item) {
      if (event.target.files && event.target.files.length > 0) {
        const selectedFile = Array.from(event.target.files)?.map(
          (file) => file
        )[0];

        // Obtener la extensión del archivo
        const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
        const allowedExtensions = ['pdf', 'jpg'];

        // Validar que la extensión esté permitida
        if (!allowedExtensions.includes(fileExtension)) {
          this.ShowToast(
            i18n.t('Error'),
            `${i18n.t('AlertOnlyPdfAttachment')}.`,
            'error'
          );
          event.target.value = '';
          return;
        }

        let formData = objectToFormData(
          {
            itemId: this.itemId,
            fileExtension: fileExtension,
            categoryId: item.id,
            fileName: selectedFile.name,
            file: selectedFile,
            isPublic: item.isPublic,
          },
          {
            indices: true,
            nullsAsUndefineds: true,
          }
        );

        await saveItemAttachment(formData);
        this.onRefreshHandler();
      }

      event.target.value = '';
    },
    async onDeleteHandler(item) {
      if (item.documentId) {
        const answer = await this.ShowCancelModal(
          'Remover Documento',
          '¿Está seguro que desea remover el documento?',
          'Si',
          'No'
        );
        if (answer) {
          await removeItemAttachment(item.documentId);
          this.ShowToast(
            'Proceso exitoso',
            'El documento ha sido removido exitosamente.',
            'success'
          );
          this.onRefreshHandler();
        }
      }
    },

    canRetryStamping(item) {
      const isAuthorized = this.itemStatus === 24;
      const isPendingOrError =
        item.stampingStatus === 'Pendiente' || item.processingError;

      return (
        item.documentId &&
        this.canManageStamping &&
        isAuthorized &&
        !this.isRedaccionCartaOrTesis &&
        isPendingOrError
      );
    },

    async retryStamping(item) {
      const confirm = await this.ShowCancelModal(
        this.$t('retryStamping'),
        `${this.$t('confirmRetry')}\n\n${this.$t('document')}: ${
          item.filename
        }\nIntentos: ${item.processingAttempts || 0}`,
        this.$t('yes'),
        this.$t('no')
      );

      if (!confirm) return;

      try {
        this.processing = true;

        await executeManualStamping(item.documentId);

        item.stampingStatus = 'Procesando';
        item.processingError = null;

        this.ShowToast(
          'Éxito',
          'El estampado se ha iniciado. Los cambios se verán en unos momentos.',
          'success'
        );

        setTimeout(() => {
          this.onRefreshHandler();
        }, 3000);
      } catch (error) {
        console.error('Error retrying stamping:', error);
        const errorMessage =
          error.response?.data?.error || 'Error al reintentar el estampado';
        this.ShowToast('', errorMessage, 'error');
      } finally {
        this.processing = false;
      }
    },

    async retryAll() {
      const pendingDocs = this.documents.filter(
        (d) =>
          d.documentId &&
          (d.stampingStatus === 'Pendiente' || d.processingError)
      );

      const confirm = await this.ShowCancelModal(
        this.$t('retryAll'),
        `${this.$t('confirmRetryAll')}\n\n${this.$t('totalDocuments')}: ${
          pendingDocs.length
        }`,
        this.$t('yes'),
        this.$t('no')
      );

      if (!confirm) return;

      try {
        this.processing = true;
        let successCount = 0;
        let errorCount = 0;

        for (const doc of pendingDocs) {
          try {
            await executeManualStamping(doc.documentId);
            doc.stampingStatus = 'Procesando';
            doc.processingError = null;
            successCount++;
          } catch (error) {
            console.error(
              `Error retrying stamping for ${doc.filename}:`,
              error
            );
            errorCount++;
          }
        }

        this.ShowToast(
          this.$t('success'),
          `${this.$t(
            'stampingQueued'
          )} - Éxitos: ${successCount}, Errores: ${errorCount}`,
          errorCount > 0 ? 'warning' : 'success'
        );

        setTimeout(() => {
          this.onRefreshHandler();
        }, 3000);
      } catch (error) {
        console.error('Error in retryAll:', error);
        this.ShowToast('', this.$t('errorRetrying'), 'error');
      } finally {
        this.processing = false;
      }
    },

    formatStampingDate(date) {
      if (!date) return '';
      return moment(date).format('DD/MM/YYYY hh:mm A');
    },
  },
};
</script>

<style scoped lang="scss">
::v-deep .delete-action {
  border-radius: 4px;
  border: 1px solid #0f556a;
  width: 38px;
  height: 38px;
  display: flex;
  justify-content: center;
  align-items: center;
  &:hover {
    background: unset;
    color: unset;
  }
  i {
    font-size: 20px;
    margin-right: 0 !important;
  }
}

::v-deep .view-action {
  border-radius: 4px;
  width: 38px;
  height: 38px;
  display: flex;
  justify-content: center;
  align-items: center;
  i {
    font-size: 20px;
    margin-right: 0 !important;
  }
}

::v-deep .download-action {
  border-radius: 4px;
  width: 38px;
  height: 38px;
  display: flex;
  justify-content: center;
  align-items: center;
  i {
    font-size: 20px;
    margin-right: 0 !important;
  }
}

::v-deep .retry-action {
  border-radius: 4px;
  border: 1px solid #dc3545;
  width: 38px;
  height: 38px;
  display: flex;
  justify-content: center;
  align-items: center;
  &:hover {
    background: #dc3545;
    color: white;
  }
  i {
    font-size: 16px;
    margin-right: 0 !important;
  }
}

.badge {
  font-size: 11px;
  padding: 0.25rem 0.5rem;
  font-weight: 500;
}

.cursor-pointer {
  cursor: pointer;
}

.invisible-button {
  visibility: hidden;
  pointer-events: none;
}
</style>
